import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Edit, 
  Save, 
  X, 
  ChevronDown, 
  Check, 
  RefreshCw,
  Search,
  UserPlus,
  UserMinus,
  AlertCircle,
  ChevronRight,
  Printer
} from 'lucide-react';
import teacherDetailsService from '../../services/teacherDetailsService';
import '../../styles/TeacherDetails.css';

const TeacherDetails = () => {
  const [assignments, setAssignments] = useState([]);
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [selectedAssignments, setSelectedAssignments] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [teacherLoads, setTeacherLoads] = useState([]);
  const [sortBy, setSortBy] = useState('subject'); // 'subject', 'class', 'teacher'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showTeacherLoad, setShowTeacherLoad] = useState(true);

  // Fallback data functions
  const getFallbackData = () => [
    {
      id: '1',
      subject_code: 'A&D',
      class_code: '1A',
      subject_name: 'Art & Design',
      class_name: '1A',
      page_number: 0,
      quota_value: 'A&D--1A--Page 0--2--PTW',
      teacher_id: 'tn_01-t001',
      teacher_name: 'Peter Wong',
      teacher_initials: 'PTW',
      teacher_load: 18,
      quota_sessions: 2,
      quota_group: 'a',
      quota_type: 'normal',
      academic_year: '2024-2025',
      semester: '1',
      status: 'active'
    },
    {
      id: '2',
      subject_code: 'AMA',
      class_code: '6S',
      subject_name: 'Applied Mathematics',
      class_name: '6S',
      page_number: 1,
      quota_value: 'AMA--6S--Page 1--6--CKK',
      teacher_id: 'tn_01-t002',
      teacher_name: 'Charles Kwok',
      teacher_initials: 'CKK',
      teacher_load: 12,
      quota_sessions: 6,
      quota_group: 'd',
      quota_type: 'normal',
      academic_year: '2024-2025',
      semester: '1',
      status: 'active'
    },
    {
      id: '3',
      subject_code: 'BIO',
      class_code: '4B',
      subject_name: 'Biology',
      class_name: '4B',
      page_number: 0,
      quota_value: 'BIO--4B--Page 0--4--CFY',
      teacher_id: 'tn_01-t004',
      teacher_name: 'Catherine Fung',
      teacher_initials: 'CFY',
      teacher_load: 20,
      quota_sessions: 4,
      quota_group: 'd',
      quota_type: 'normal',
      academic_year: '2024-2025',
      semester: '1',
      status: 'active'
    },
    {
      id: '4',
      subject_code: 'CCP',
      class_code: '1A',
      subject_name: 'Computer Programming',
      class_name: '1A',
      page_number: 0,
      quota_value: 'CCP--1A--Page 0--2--THS',
      teacher_id: 'tn_01-t005',
      teacher_name: 'Thomas Ho',
      teacher_initials: 'THS',
      teacher_load: 14,
      quota_sessions: 2,
      quota_group: 'a',
      quota_type: 'normal',
      academic_year: '2024-2025',
      semester: '1',
      status: 'active'
    },
    {
      id: '5',
      subject_code: 'CHI',
      class_code: '1A',
      subject_name: 'Chinese',
      class_name: '1A',
      page_number: 0,
      quota_value: 'CHI--1A--Page 0--6--CCH',
      teacher_id: 'tn_01-t009',
      teacher_name: 'Carol Chan',
      teacher_initials: 'CCH',
      teacher_load: 18,
      quota_sessions: 6,
      quota_group: 'd',
      quota_type: 'normal',
      academic_year: '2024-2025',
      semester: '1',
      status: 'active'
    }
  ];

  const getFallbackTeachers = () => [
    { id: 'tn_01-t001', name: 'Peter Wong', english_name: 'Peter Wong' },
    { id: 'tn_01-t002', name: 'Charles Kwok', english_name: 'Charles Kwok' },
    { id: 'tn_01-t004', name: 'Catherine Fung', english_name: 'Catherine Fung' },
    { id: 'tn_01-t005', name: 'Thomas Ho', english_name: 'Thomas Ho' },
    { id: 'tn_01-t009', name: 'Carol Chan', english_name: 'Carol Chan' }
  ];

  const getFallbackTeacherLoads = () => [
    { teacher_id: 'tn_01-t001', teacher_name: 'Peter Wong', teacher_initials: 'PTW', total_load: 18 },
    { teacher_id: 'tn_01-t002', teacher_name: 'Charles Kwok', teacher_initials: 'CKK', total_load: 12 },
    { teacher_id: 'tn_01-t004', teacher_name: 'Catherine Fung', teacher_initials: 'CFY', total_load: 20 },
    { teacher_id: 'tn_01-t005', teacher_name: 'Thomas Ho', teacher_initials: 'THS', total_load: 14 },
    { teacher_id: 'tn_01-t009', teacher_name: 'Carol Chan', teacher_initials: 'CCH', total_load: 18 }
  ];

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  // Filter and sort data when dependencies change
  useEffect(() => {
    filterAndSortData();
  }, [assignments, searchTerm, sortBy]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [assignmentsData, teachersData, teacherLoadsData] = await Promise.all([
        teacherDetailsService.getTeacherAssignmentsSorted(sortBy),
        teacherDetailsService.getAvailableTeachers(),
        teacherDetailsService.getTeacherLoadSummary()
      ]);
      
      console.log('Loaded data:', { assignmentsData, teachersData, teacherLoadsData });
      
      // If no data from Supabase, use fallback sample data
      if (!assignmentsData || assignmentsData.length === 0) {
        console.log('No data from Supabase, using fallback data');
        const fallbackData = getFallbackData();
        setAssignments(fallbackData);
        setTeachers(getFallbackTeachers());
        setTeacherLoads(getFallbackTeacherLoads());
      } else {
        setAssignments(assignmentsData);
        setTeachers(teachersData);
        setTeacherLoads(teacherLoadsData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      // Use fallback data on error
      const fallbackData = getFallbackData();
      setAssignments(fallbackData);
      setTeachers(getFallbackTeachers());
      setTeacherLoads(getFallbackTeacherLoads());
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortData = () => {
    let filtered = assignments.filter(assignment => 
      assignment.subject_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.class_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (assignment.teacher_name && assignment.teacher_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (assignment.teacher_initials && assignment.teacher_initials.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Sort data
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'class':
          return a.class_code.localeCompare(b.class_code) || a.subject_code.localeCompare(b.subject_code);
        case 'teacher':
          return (a.teacher_name || '').localeCompare(b.teacher_name || '') || a.subject_code.localeCompare(b.subject_code);
        default:
          return a.subject_code.localeCompare(b.subject_code) || a.class_code.localeCompare(b.class_code);
      }
    });

    setFilteredAssignments(filtered);
  };

  const handleAssignmentSelect = (assignment) => {
    setSelectedAssignments(prev => {
      const isSelected = prev.some(selected => selected.id === assignment.id);
      if (isSelected) {
        return prev.filter(selected => selected.id !== assignment.id);
      } else {
        return [...prev, assignment];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedAssignments.length === filteredAssignments.length) {
      setSelectedAssignments([]);
    } else {
      setSelectedAssignments([...filteredAssignments]);
    }
  };

  const handleModify = () => {
    if (selectedAssignments.length === 0) {
      alert('請先選擇要編配教師的班別科目 (Please select class subjects to assign teachers)');
      return;
    }
    setIsEditing(true);
  };

  const handleAssignTeacher = async () => {
    if (!selectedTeacher) {
      alert('請選擇教師 (Please select a teacher)');
      return;
    }

    if (selectedAssignments.length === 0) {
      alert('請先選擇要編配教師的班別科目 (Please select class subjects to assign teachers)');
      return;
    }

    setLoading(true);
    try {
      const promises = selectedAssignments.map(assignment => 
        teacherDetailsService.assignTeacher(
          assignment.subject_code,
          assignment.class_code,
          selectedTeacher,
          assignment.page_number || 0
        )
      );

      await Promise.all(promises);
      
      // Reload data to reflect changes
      await loadData();
      setSelectedAssignments([]);
      setSelectedTeacher('');
      setIsEditing(false);
      
      alert('教師編配完成 (Teacher assignment completed)');
    } catch (error) {
      console.error('Error assigning teacher:', error);
      alert('編配失敗，請重試 (Assignment failed, please try again)');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveTeacher = async (assignmentId) => {
    const confirmed = window.confirm('確定要移除教師編配嗎？ (Are you sure you want to remove the teacher assignment?)');
    if (confirmed) {
      try {
        await teacherDetailsService.removeTeacherAssignment(assignmentId);
        await loadData();
        alert('教師編配已移除 (Teacher assignment removed)');
      } catch (error) {
        console.error('Error removing teacher assignment:', error);
        alert('移除失敗，請重試 (Removal failed, please try again)');
      }
    }
  };

  const handleCancel = () => {
    setSelectedAssignments([]);
    setSelectedTeacher('');
    setIsEditing(false);
  };

  const formatAssignmentDisplay = (assignment) => {
    return `${assignment.subject_code}--${assignment.class_code}--Page ${assignment.page_number || 0}--${assignment.quota_sessions || 0}--${assignment.teacher_initials || 'UNASSIGNED'}`;
  };

  if (loading && assignments.length === 0) {
    return (
      <div className="teacher-details-container">
        <div className="loading-container">
          <div className="loading-content">
            <RefreshCw className="loading-spinner" size={48} />
            <p className="loading-text">載入教師資料中... (Loading teacher data...)</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="teacher-details-container">
      {/* Header */}
      <div className="teacher-details-header">
        <h2>班別科目配額詳情編修 - 教師 (Class-Subject Item Quota Details - Teachers)</h2>
        <p className="subtitle">TBM0180E</p>
      </div>

      {/* Search and Filter Controls */}
      <div className="search-filter-section">
        <div className="search-filter-controls">
          <div className="search-input">
            <Search className="search-icon" size={16} />
            <input
              type="text"
              placeholder="搜尋科目、班別或教師 (Search subjects, classes or teachers)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="sort-controls">
            <label className="sort-label">排序方式 (Sort by):</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="subject">依科目排列 (By Subject)</option>
              <option value="class">依班別排列 (By Class)</option>
              <option value="teacher">依教師排列 (By Teacher)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="teacher-details-content">
        {/* Left Panel - Assignment List */}
        <div className="assignment-list-panel">
          <div className="assignment-list-header">
            <h3 className="assignment-list-title">SubjItem-Class-PageNo-Quota-Teacher</h3>
            <div className="assignment-counter">
              {selectedAssignments.length}/{filteredAssignments.length}
            </div>
          </div>
          
          <div className="assignment-list">
            {filteredAssignments.map((assignment) => (
              <div
                key={assignment.id}
                onClick={() => handleAssignmentSelect(assignment)}
                className={`assignment-row ${
                  selectedAssignments.some(selected => selected.id === assignment.id) 
                    ? 'selected' 
                    : ''
                }`}
              >
                <div>
                  <div className="assignment-code">{formatAssignmentDisplay(assignment)}</div>
                  <div className="assignment-details">
                    {assignment.subject_name} - {assignment.class_name}
                    {assignment.teacher_name && ` - ${assignment.teacher_name}`}
                  </div>
                </div>
                <div className="assignment-actions">
                  {assignment.teacher_id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveTeacher(assignment.id);
                      }}
                      className="remove-teacher-btn"
                      title="移除教師編配 (Remove teacher assignment)"
                    >
                      <UserMinus size={14} />
                    </button>
                  )}
                  <Check className="assignment-check" size={16} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Center Panel - Selected Items and Teacher Input */}
        <div className="teacher-controls-panel">
          {/* Selected Items */}
          <div className="control-section">
            <h4 className="control-section-title">Selected</h4>
            <div className="selected-items-display">
              {selectedAssignments.length === 0 ? (
                <div className="no-selection">
                  No item selected
                </div>
              ) : (
                selectedAssignments.map((assignment) => (
                  <div key={assignment.id} className="selected-item">
                    {formatAssignmentDisplay(assignment)}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Teacher Selection */}
          <div className="control-section">
            <h4 className="control-section-title">Teacher</h4>
            <div className="teacher-input">
              <select
                value={selectedTeacher}
                onChange={(e) => setSelectedTeacher(e.target.value)}
                className="teacher-field"
              >
                <option value="">Select Teacher</option>
                {teachers.map(teacher => (
                  <option key={teacher.id} value={teacher.id}>
                    {teacher.name} ({teacher.english_name || 'N/A'})
                  </option>
                ))}
              </select>
              <button className="teacher-dropdown">
                <ChevronDown size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Right Panel - Teacher Load and Action Buttons */}
        <div className="teacher-load-panel">
          {/* Teacher Load */}
          <div className="teacher-load-header">
            <h4 className="teacher-load-title">Teacher Load</h4>
          </div>
          
          <div className="teacher-load-list">
            {teacherLoads.map((teacherLoad) => (
              <div key={teacherLoad.teacher_id} className="teacher-load-item">
                <span className="teacher-load-code">{teacherLoad.teacher_initials}--{teacherLoad.total_load}</span>
                <span className="teacher-load-name">{teacherLoad.teacher_name}</span>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button className="action-button secondary">
              Close
            </button>
            <button className="action-button secondary">
              <ChevronRight size={16} />
              Next Page
            </button>
            <button
              onClick={handleModify}
              disabled={selectedAssignments.length === 0}
              className="action-button primary"
            >
              <Edit size={16} />
              Modify
            </button>
            
            {isEditing && (
              <div className="action-button-group">
                <button
                  onClick={handleCancel}
                  className="action-button secondary"
                >
                  <X size={16} />
                  Cancel
                </button>
                <button
                  onClick={handleAssignTeacher}
                  disabled={!selectedTeacher}
                  className="action-button success"
                >
                  <Save size={16} />
                  Save
                </button>
              </div>
            )}
            
            <button className="action-button secondary">
              <Printer size={16} />
              Print
            </button>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="instructions-section">
        <h5 className="instructions-title">使用說明 (Instructions):</h5>
        <ol className="instructions-list">
          <li>使用者可利用下拉式選單選擇依科目排列、依班別排列或依教師排列</li>
          <li>在科目列表中選取需編配教師的班別科目 (可同時選取多項班別科目)</li>
          <li>按「編修」鍵編修班別科目授課教師</li>
          <li>從「教師」選單中選擇欲編配課節的教師。教師授課節數表只作參考，用者並不能在此點選教師</li>
          <li>輸入完成後，按「儲存」鍵紀錄資料或按「取消」鍵取消儲存資料</li>
          <li>按「下頁」鍵進入編配課室資料畫面</li>
          <li>系統會顯示編配結果和教師負荷情況</li>
        </ol>
      </div>
    </div>
  );
};

export default TeacherDetails;
